import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UnifiedAuthProvider } from "./contexts/UnifiedAuthContext";
import { AdminProvider } from "./contexts/AdminContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Login page removed - use OAuth callback instead
import Home from "./pages/Home";
import Games from "./pages/Games";
import TopUp from "./pages/TopUp";
import Credit from "./pages/Credit";
import About from "./pages/About";
import Support from "./pages/Support";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Refund from "./pages/Refund";
import Orders from "./pages/Orders";
import LineCallback from "./pages/LineCallback";
import PlayXpointHistory from "./pages/PlayXpointHistory";


// Admin pages
import AdminLayout from "./components/AdminLayout";
import AdminDashboard from "./pages/AdminDashboard";
import AdminGameList from "./pages/AdminGameList";
import AdminUsers from "./pages/AdminUsers";
import AdminTransactions from "./pages/AdminTransactions";
import AdminAuditLog from "./pages/AdminAuditLog";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminAppeals from "./pages/admin/AdminAppeals";
import AdminAppealAnalytics from "./pages/admin/AdminAppealAnalytics";
import AdminSMSDashboard from "./pages/AdminSMSDashboard";
import AdminAbuseAlerts from "./pages/AdminAbuseAlerts";
import SMSNotificationHistory from "./pages/SMSNotificationHistory";
import UserAppeals from "./pages/UserAppeals";
import UserSettings from "./pages/UserSettings";
import AdminProfitDashboard from "./pages/AdminProfitDashboard";
import AdminPEAKExport from "./pages/AdminPEAKExport";
import AdminGameDetail from "./pages/AdminGameDetail";
import AdminEditGameImage from "./pages/AdminEditGameImage";
import AdminGameSync from "./pages/AdminGameSync";
import TesterLogin from "./pages/TesterLogin";

function AdminRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  return <Component />;
}

function Router() {
  return (
    <Switch>
      {/* All routes require authentication on Firebase domain */}
      <Route path="/">
        {() => (
          <ProtectedRoute>
            <Home />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/about">
        {() => (
          <ProtectedRoute>
            <About />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/support">
        {() => (
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/terms">
        {() => (
          <ProtectedRoute>
            <Terms />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/privacy">
        {() => (
          <ProtectedRoute>
            <Privacy />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/refund">
        {() => (
          <ProtectedRoute>
            <Refund />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/auth/line/callback" component={LineCallback} />

      {/* Protected routes */}
      <Route path="/games">
        {() => (
          <ProtectedRoute>
            <Games />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/topup">
        {() => (
          <ProtectedRoute>
            <TopUp />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/topup/:gameKey">
        {() => (
          <ProtectedRoute>
            <TopUp />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/games/:gameKey">
        {() => (
          <ProtectedRoute>
            <TopUp />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/credit">
        {() => (
          <ProtectedRoute>
            <Credit />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/orders">
        {() => (
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/appeals">
        {() => (
          <ProtectedRoute>
            <UserAppeals />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/settings">
        {() => (
          <ProtectedRoute>
            <UserSettings />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/playxpoint-history">
        {() => (
          <ProtectedRoute>
            <PlayXpointHistory />
          </ProtectedRoute>
        )}
      </Route>

      <Route path="/sms-history">
        {() => (
          <ProtectedRoute>
            <SMSNotificationHistory />
          </ProtectedRoute>
        )}
      </Route>

      {/* Admin routes (protected with admin role) */}
      <Route path="/admin">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminDashboard} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/games">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminGameList} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/games/:gameKey">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminGameDetail} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/games/:gameKey/edit-image">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminEditGameImage} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/users">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminUsers} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/transactions">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminTransactions} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/orders">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminTransactions} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/audit-log">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminAuditLog} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/settings">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminSettings} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/appeals">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminAppeals} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/appeal-analytics">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminAppealAnalytics} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/sms">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminSMSDashboard} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/sms/abuse-alerts">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminAbuseAlerts} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/profit-dashboard">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminProfitDashboard} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/peak-export">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminPEAKExport} />
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/admin/game-sync">
        {() => (
          <ProtectedRoute requiredRole="admin">
            <AdminRoute component={AdminGameSync} />
          </ProtectedRoute>
        )}
      </Route>

      {/* Login route for UAT/Firebase environments */}
      <Route path="/login">
        {() => <TesterLogin />}
      </Route>

      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <UnifiedAuthProvider>
        <AdminProvider>
          <ThemeProvider defaultTheme="dark">
            <TooltipProvider>
              <Toaster />
              <Router />
            </TooltipProvider>
          </ThemeProvider>
        </AdminProvider>
      </UnifiedAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
